import { FormEvent, useEffect, useState } from "react";
import { ArrowRight, CheckCircle2, ClipboardList, FileText, LockKeyhole, Mail, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { SiteLayout } from "@/components/SiteChrome";
import { trpc } from "@/lib/trpc";

/**
 * FitLife-X Pro: public sales page, sign-in, and post-checkout landing.
 *
 * Note on markup: SiteLayout already renders a <main>. The older pages nest a
 * second one inside it, which is invalid; these use a <div> so the mistake is
 * not carried any further.
 */

const PILLARS = [
  {
    icon: FileText,
    title: "White-label forms library",
    body: "Waivers, health screening declarations, incident reports, inspection logs, staff checklists. Prepared under your brand, your colours, your entity name — ready to put in front of a member the day you receive them.",
  },
  {
    icon: ClipboardList,
    title: "SOP development",
    body: "A branded operating standard built on one of three prebuilt models — independent site, multi-site operator, or building amenity. You review the base document, answer a questionnaire, and get it back written around your facility.",
  },
  {
    icon: Megaphone,
    title: "Advertising placement",
    body: "Put your product or service in front of operators who are actively buying. Tell us what you sell and where you want it seen, and we will come back with placement options and a quote.",
  },
];

/* -------------------------------------------------------------------------- */
/* Sales page                                                                 */
/* -------------------------------------------------------------------------- */

export default function FitLifeX() {
  const plans = trpc.fitlifeX.plans.useQuery();
  const me = trpc.fitlifeX.me.useQuery();
  const [email, setEmail] = useState("");
  const [plan, setPlan] = useState<"monthly" | "annual">("annual");

  const checkout = trpc.fitlifeX.startCheckout.useMutation({
    onSuccess: data => window.location.assign(data.url),
    onError: error => toast.error("Checkout could not be opened", { description: error.message }),
  });

  const available = plans.data?.available ?? [];
  // Default to whichever plan exists if the preferred one is not configured,
  // so the form is never pre-set to something that cannot be bought.
  const selectable = available.some(entry => entry.plan === plan) ? plan : available[0]?.plan;

  function onSubscribe(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectable) return;
    checkout.mutate({ email: email.trim(), plan: selectable });
  }

  return (
    <SiteLayout>
      <div className="fx-page">
        <section className="fx-hero">
          <div className="site-container">
            <p className="eyebrow eyebrow--light">FitLife-X Pro</p>
            <h1>
              The operating layer
              <br />
              <em>behind the newsletter.</em>
            </h1>
            <p>
              FitLife-X stays free. Pro is for operators who want the documents, procedures and
              placement that sit behind it — prepared for your facility rather than read and set aside.
            </p>
          </div>
        </section>

        <section className="section">
          <div className="site-container">
            <div className="section-head section-head--center">
              <p className="eyebrow">What is included</p>
              <h2>
                Three things you would
                <br />
                <em>otherwise build yourself.</em>
              </h2>
            </div>
            <div className="fx-pillars">
              {PILLARS.map(pillar => (
                <article key={pillar.title}>
                  <span className="fx-pillars__icon">
                    <pillar.icon size={20} />
                  </span>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section--soft" id="subscribe">
          <div className="site-container fx-subscribe">
            <div>
              <p className="eyebrow">Membership</p>
              <h2>
                Start your
                <br />
                <em>Pro membership.</em>
              </h2>
              <ul className="fx-benefits">
                <li><CheckCircle2 size={15} />Everything in the free newsletter</li>
                <li><CheckCircle2 size={15} />White-label forms prepared under your brand</li>
                <li><CheckCircle2 size={15} />A branded SOP built around your facility</li>
                <li><CheckCircle2 size={15} />Priority consideration for advertising placement</li>
                <li><CheckCircle2 size={15} />Cancel from your billing portal at any time</li>
              </ul>
            </div>

            <form className="fx-plan-form" onSubmit={onSubscribe}>
              {plans.isLoading && <p className="fx-plan-form__note">Loading plans…</p>}

              {/* No plans configured is a setup state, not an error the visitor
                  caused - say so plainly and give them a way to reach you. */}
              {!plans.isLoading && available.length === 0 && (
                <div className="fx-plan-empty">
                  <LockKeyhole size={18} />
                  <p>Pro memberships are not open for signup just yet. Email <a href="mailto:info@altitudefitlife.com">info@altitudefitlife.com</a> and we will let you know the moment they are.</p>
                </div>
              )}

              {available.map(entry => (
                <label key={entry.plan} className={`fx-plan ${selectable === entry.plan ? "is-selected" : ""}`}>
                  <input
                    type="radio"
                    name="plan"
                    value={entry.plan}
                    checked={selectable === entry.plan}
                    onChange={() => setPlan(entry.plan)}
                  />
                  <span className="fx-plan__body">
                    <strong>{entry.label.replace("FitLife-X Pro — ", "")}</strong>
                    {/* Price comes from Stripe, never from this file, so the
                        page cannot show one number while checkout charges another. */}
                    <span className="fx-plan__price">{entry.display ?? "See price at checkout"}</span>
                    <small>{entry.cadence}</small>
                  </span>
                </label>
              ))}

              {available.length > 0 && (
                <>
                  <label className="field">
                    <span>Email address *</span>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      placeholder="you@yourfacility.com"
                      value={email}
                      onChange={event => setEmail(event.target.value)}
                    />
                  </label>
                  <button className="button button--coral button--full" disabled={checkout.isPending || !selectable}>
                    {checkout.isPending ? "Opening secure checkout…" : <>Continue to Stripe<ArrowRight size={16} /></>}
                  </button>
                  <p className="fx-plan-form__note">
                    <LockKeyhole size={13} />
                    Billing is handled by Stripe. We never see your card details.
                  </p>
                </>
              )}

              {me.data?.signedIn ? (
                <a className="fx-plan-form__link" href="/fitlife-x/portal">Already a member — open my workspace</a>
              ) : (
                <a className="fx-plan-form__link" href="/fitlife-x/login">Already a member? Sign in</a>
              )}
            </form>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Sign in                                                                    */
/* -------------------------------------------------------------------------- */

export function FitLifeXLogin() {
  const [sent, setSent] = useState(false);
  // Read in an effect, not during render. wouter's useLocation gives the
  // pathname without the query string, and reaching for window.location during
  // render makes the server pass and the first client render disagree - React
  // reports that as a hydration mismatch and discards the server markup.
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    setExpired(new URLSearchParams(window.location.search).has("expired"));
  }, []);

  const requestLogin = trpc.fitlifeX.requestLogin.useMutation({
    onSuccess: () => setSent(true),
    onError: error => toast.error("That did not go through", { description: error.message }),
  });

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    requestLogin.mutate({ email: String(form.get("email") ?? "") });
  }

  return (
    <SiteLayout>
      <div className="fx-page fx-page--narrow">
        <section className="section">
          <div className="site-container fx-auth">
            <p className="eyebrow">FitLife-X Pro</p>
            <h1>Sign in.</h1>

            {expired && !sent && (
              <div className="fx-auth__warning">
                That sign-in link has already been used or has expired. Request a new one below.
              </div>
            )}

            {sent ? (
              /* Deliberately the same message whether or not the address is on
                 file. Confirming which addresses exist would turn this form
                 into a subscriber list for anyone who wanted one. */
              <div className="fx-auth__sent">
                <Mail size={24} />
                <h2>Check your email.</h2>
                <p>
                  If that address has a FitLife-X account, a sign-in link is on its way. It works once
                  and expires in 20 minutes.
                </p>
              </div>
            ) : (
              <>
                <p>
                  No password. Enter your email and we will send a link that signs you straight in.
                </p>
                <form onSubmit={onSubmit}>
                  <label className="field">
                    <span>Email address *</span>
                    <input name="email" type="email" inputMode="email" autoComplete="email" required placeholder="you@yourfacility.com" autoFocus />
                  </label>
                  <button className="button button--coral button--full" disabled={requestLogin.isPending}>
                    {requestLogin.isPending ? "Sending…" : "Email me a sign-in link"}
                  </button>
                </form>
                <a className="fx-auth__link" href="/fitlife-x/pro">Not a member yet? See what Pro includes</a>
              </>
            )}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}

/* -------------------------------------------------------------------------- */
/* Post-checkout landing                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Where Stripe returns a new subscriber.
 *
 * This page grants nothing. Entitlement is written by the subscription webhook,
 * which may land a second or two after this redirect, so the page sends them to
 * sign in rather than pretending to know their status. Treating the redirect as
 * proof of payment is the classic way a free account gets created by anyone who
 * can guess the success URL.
 */
export function FitLifeXWelcome() {
  return (
    <SiteLayout>
      <div className="fx-page fx-page--narrow">
        <section className="section">
          <div className="site-container fx-auth">
            <span className="fx-auth__tick"><CheckCircle2 size={28} /></span>
            <p className="eyebrow">FitLife-X Pro</p>
            <h1>You are in.</h1>
            <p>
              Stripe has your subscription. To open your workspace, sign in with the same email you
              used at checkout — we will send a one-time link, no password to set.
            </p>
            <a className="button button--coral button--full" href="/fitlife-x/login">
              Email me a sign-in link<ArrowRight size={16} />
            </a>
            <p className="fx-auth__fineprint">
              Your receipt and invoices are in Stripe, and you can manage or cancel the subscription
              from the billing portal inside your workspace at any time.
            </p>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}
